import { useEffect, useState, useCallback } from "react";
import { useQuery } from "react-query";
import axios from "axios";

import api from "@api/index";
import { IResponseDataDto } from "@interfaces/rest/response.dto";
import {
  IActiveThemeDto,
  IApiDataDto,
  IExperienceDeviceDto,
  IGalleryConfigDto,
  IThemeStyle,
  IThemeStyleInstance,
} from "@interfaces/rest/apiData.dto";
import {
  IApiDataConfig,
  IApiDataState,
  IStyleConfig,
} from "@interfaces/types/apiData.type";
import {
  ConfigClientEnum,
  ClientCompanyNameEnum,
  VenueNameEnum,
} from "@interfaces/enums/apiData.enum";
import snStyles from "@theme/sn.json";
import cggStyles from "@theme/cgg.json";
import {
  parseBarcodeFromUrl,
  parsePhoneNumberFromUrl,
} from "@helpers/validation";

interface IActiveThemeProps {
  url: string;
  client: ConfigClientEnum;
}

const useConfigState = (): IApiDataState => {
  const [config, setConfig] = useState<IApiDataConfig | null>(null);
  const [activeTheme, setActiveTheme] = useState<IActiveThemeDto | null>(null);

  const fetchConfig = async (): Promise<IApiDataDto> => {
    const response = await axios.get(api.config);
    return response.data;
  };

  const { data: configData } = useQuery<IApiDataDto>("getConfig", fetchConfig);

  const fetchActiveTheme = async ({
    url,
    client,
  }: IActiveThemeProps): Promise<IActiveThemeDto | null> => {
    const response = await axios.get<IResponseDataDto<IActiveThemeDto>>(
      api.activeTheme(client),
      { baseURL: url }
    );

    return response.data.data;
  };

  const { data: activeThemeData } = useQuery<IActiveThemeDto | null>(
    ["getActiveTheme", configData?.CaptureAPI_URI, config?.client],
    () =>
      fetchActiveTheme({
        url: configData!.CaptureAPI_URI,
        client: config!.client,
      }),
    {
      enabled: !!configData && !!config,
    }
  );

  useQuery<IExperienceDeviceDto | null>(
    [
      "getExperienceDevice",
      config?.isKiosk,
      config?.deviceUuid,
      config?.CaptureAPI_URI,
    ],
    async () => {
      const response = await axios.get<{
        data: IExperienceDeviceDto;
        isSuccess: boolean;
      }>(config!.CaptureAPI_URI + api.experienceDevice(config!.deviceUuid!));
      return response.data.isSuccess ? response.data.data : null;
    },
    {
      enabled: !!config?.isKiosk && !!config?.deviceUuid,
      onSuccess: (data) => {
        if (data) {
          setConfig((prev) =>
            prev
              ? {
                  ...prev,
                  isGuestbookAnimation:
                    prev.isGuestbookAnimation ||
                    data.device.settings.isEnableSkyPadAnimation,
                }
              : prev
          );
        }
      },
    }
  );

  useQuery<IGalleryConfigDto | null>(
    ["getGalleryConfig", config?.CaptureAPI_URI],
    async () => {
      const response = await axios.get<IResponseDataDto<IGalleryConfigDto>>(
        api.galleryConfig,
        { baseURL: config!.CaptureAPI_URI }
      );
      return response.data.isSuccess ? response.data.data : null;
    },
    {
      enabled: !!config,
      refetchInterval: 5 * 60 * 1000,
      onSuccess: (data) => {
        if (data) {
          setConfig((prev) =>
            prev ? { ...prev, isSkypadEnabled: data.isSkypadEnabled } : prev
          );
        }
      },
    }
  );

  useEffect(() => {
    if (configData) {
      const params = new URLSearchParams(document.location.search);

      const clientDomain = window.location.hostname.split(
        "-"
      )[0] as ConfigClientEnum;
      const beforeUrl: ConfigClientEnum = Object.keys(
        ConfigClientEnum
      ).includes(clientDomain)
        ? clientDomain
        : ConfigClientEnum.sn;

      const client: ConfigClientEnum =
        window.location.hostname === "localhost"
          ? ConfigClientEnum.sn
          : beforeUrl;

      const userAgent =
        window.navigator.userAgent.toLowerCase() ||
        window.navigator.vendor.toLowerCase();

      const phoneNumber = parsePhoneNumberFromUrl(params.get("phoneNumber"));
      const barcode = parseBarcodeFromUrl(params.get("barcode"));

      const isGuestbookAnimation =
        params.get("isGuestbookAnimation") === "true";

      if (params.has("isGuestbookAnimation")) {
        params.delete("isGuestbookAnimation");
        window.history.replaceState(
          null,
          "",
          `${window.location.pathname}${params.toString() ? `?${params}` : ""}`
        );
      }

      setConfig({
        API_URI: configData.API_URI,
        CaptureAPI_URI: configData.CaptureAPI_URI,
        appKey: configData.appKey,
        googleMapsApiKey: configData.REACT_APP_GOOGLE_MAPS_API_KEY,
        client,
        companyName: ClientCompanyNameEnum[client],
        venueName: VenueNameEnum[client],
        isKiosk: params.get("mode") === "kiosk",
        isAndroid: /android/i.test(userAgent),
        isNewGalleryView: configData.NEW_GALLERY_VIEW === "true",
        providedBarcode: barcode ?? undefined,
        providedPhoneNumber: phoneNumber ?? undefined,
        deviceUuid: params.get("deviceUuid") ?? undefined,
        isGuestbookAnimation,
      });
    }
  }, [configData]);

  useEffect(() => {
    if (activeThemeData) {
      setActiveTheme(activeThemeData);

      if (config && !config.isKiosk) {
        document.styleSheets[0].insertRule(
          `:root{--background:url(${activeThemeData.backgroundImage})}`
        );

        const iconLinkDomObj = document.querySelector(
          'link[rel="icon"][type="image/x-icon"]'
        ) as HTMLLinkElement;

        if (iconLinkDomObj && activeThemeData.faviconImageLow) {
          iconLinkDomObj.href = activeThemeData.faviconImageLow;
        }
      }
    }
  }, [activeThemeData, config]);

  useEffect(() => {
    if (config) {
      const themes = {
        sn: snStyles,
        cgg: cggStyles,
      };
      const currentThemeConfig: IStyleConfig = themes[config.client];
      type StyleConfigPropsArray = Array<keyof IStyleConfig>;
      const propsArray: StyleConfigPropsArray = Object.keys(
        currentThemeConfig
      ) as StyleConfigPropsArray;

      propsArray.forEach((keyName: keyof IStyleConfig) => {
        document.styleSheets[0].insertRule(
          `:root{${keyName}:${currentThemeConfig[keyName]}}`
        );
      });
    }
  }, [config?.client]);

  const transformStyleColorsToVars = useCallback((styles: IThemeStyle) => {
    type StyleKeysArray = Array<keyof IThemeStyle>;
    const propsArray: StyleKeysArray = Object.keys(styles) as StyleKeysArray;
    propsArray.forEach((keyName: keyof IThemeStyle) => {
      type StyleInstanceKeyArray = Array<keyof IThemeStyleInstance>;
      const stylnstanceKeyArray: StyleInstanceKeyArray = Object.keys(
        styles[keyName]
      ) as StyleInstanceKeyArray;
      stylnstanceKeyArray.forEach((instanceKey: keyof IThemeStyleInstance) => {
        document.styleSheets[0].insertRule(
          `:root{--${keyName}-${instanceKey}:${styles[keyName][instanceKey]}}`
        );
      });
    });
  }, []);

  useEffect(() => {
    if (activeTheme) {
      transformStyleColorsToVars(activeTheme.style);
    }
  }, [activeTheme]);

  return {
    config,
    activeTheme,
  };
};

export default useConfigState;
